import React from "react"
import {apiSavedRoomsSave, apiSolutionLoad, apiSolutionSave} from "./Api"
import {getProductElementProp, isWallToWallMove} from "./SolutionHelper"
import {getVariants} from "./Catalog"
import {useCanvasHandler} from "./CanvasHandler"
import {useRefState} from "./RefStateHook"
import produce from "immer"

const initState = {
    solutionIndex: 0,
    plannings: [],
    roomId: null,
    version: 1,
    code: null,
    savedRoom: null,
}

export const useSolution = (local, messenger) => {
    const state = useRefState(initState, 'plannings')
    const handler = useCanvasHandler()

    const apiSave = async (plan, index = null, codeSave = true) => {
        if (!getRoomId()) return false

        try {
            const response = await apiSolutionSave(
                getRoomId(),
                plan.id,
                plan.name,
                plan.products,
                local.getLang(true)
            )

            if (response["hasErrors"]) {
                const error = response["messages"][0]
                const isPlacementError = error.code === "error.placing.product"
                if (!isPlacementError) {
                    const replacements = error.context
                    const message = error.message.replace(/(\{|\})/g, "%")
                    throw new Error(local.__(message, replacements))
                }
            }

            const planningResponse = {
                id: response["id"],
                isPremium: response["isPremium"],
                updatedAt: response["updatedAt"],
                initialRevision: response["initialRevision"],
                name: response["name"],
                products: response["products"],
            }

            if (plan.id) {
                state.setState(produce(state.getCurrent(), draft => {
                    draft.plannings[index !== null ? index : getPlanningIndex()] = planningResponse
                }))
            } else {
                state.setState(produce(state.getCurrent(), draft => {
                    draft.plannings.push(planningResponse)
                }))
            }

            if(codeSave) {
                await apiSavedRooms()
            }

            return true
        } catch (error) {
            messenger.showError(local.__(error.message))
        }

        return false
    }

    const apiSavedRooms = async () => {
        const responseData = await apiSavedRoomsSave(getId(), getPlanningIds(), "", getCode())

        state.setState(produce(state.getCurrent(), draft => {
            draft.savedRoom = responseData
        }))

        return true
    }

    const save = async () => {
        if (!countPlannings()) {
            await addPlanning([], 'Leer')
        } else {
            for (let index = 0; index < state.getCurrent().plannings.length; index++) {
                await apiSave(state.getCurrent().plannings[index], index, false)
            }
            await apiSavedRooms()
        }

        return getPlanningIds()
    }

    const reset = () => {
        if (JSON.stringify(initState) === JSON.stringify(state.getCurrent())) {
            return
        }
        state.setState(initState)
    }

    const setPlannings = (roomId, plannings) => {
        setRoomId(roomId)
        state.setState(produce(state.getCurrent(), draft => {
            draft.solutionIndex = 0
            draft.plannings = plannings.map(planning => {
                return {
                    id: planning.id,
                    isPremium: planning.isPremium,
                    updatedAt: planning.timestamp || Date.now(),
                    initialRevision: planning.initialRevision,
                    name: planning.name || "noname",
                    products: planning.products,
                }
            })
        }))
    }

    const getPlanningIds = () => {
        return state.getCurrent().plannings.map(element => element.id)
    }

    const setRoomId = (roomId) => {
        if (roomId === getRoomId()) {
            return
        }
        state.setState(produce(state.getCurrent(), draft => {
            draft.roomId = roomId
        }))
    }

    const getRoomId = () => {
        return state.getCurrent().roomId
    }

    const setCode = (code) => {
        if (code === getCode()) {
            return
        }
        state.setState(produce(state.getCurrent(), draft => {
            draft.code = code
        }))
    }

    const getCode = () => {
        return state.getCurrent().savedRoom?.code
    }

    const getId = () => {
        return getPlanning()?.id
    }

    const getSavedRoom = () => {
        return state.getCurrent().savedRoom
    }

    const getVersion = () => {
        return state.getCurrent().version
    }

    const updateVersion = () => {
        const callback = () => state.setState(produce(state.getCurrent(), draft => {
            draft.version = draft.version + 1
        }))

        setTimeout(() => callback(), 100)
    }

    const productIncreaseQty = async (productId) => {
        const product = getProduct(productId)
        if (!product) return

        await productSetQty(product.quantity + 1, product.id)
    }

    const hideElement = async () => {
        const product = getProduct()
        if (!product) return false

        // delete product if all-1 elements are hidden
        if (product.elements.filter(element => element.isHidden === false).length === 1) {
            return await deleteProduct()
        }

        const planning = produce(getPlanning(), draft => {
            draft.products[getProductIndex()].elements[handler.handlerGetElementIndex()].isHidden = true
        })

        if (await apiSave(planning)) {
            handler.handlerUnset()
            updateVersion()
            return true
        }

        return false
    }

    const deleteProduct = async () => {
        const product = getProduct()
        if (!product) return false

        const planning = produce(getPlanning(), draft => {
            draft.products = draft.products.filter(element => element.id !== getProduct().id)
        })

        if (await apiSave(planning)) {
            handler.handlerUnset()
            updateVersion()
            return true
        }

        return false
    }

    const deleteProductByType = async () => {
        const product = getProduct()
        if (!product) return false

        const planning = produce(getPlanning(), draft => {
            draft.products = draft.products.filter(element => element.sku !== product.sku)
        })

        if (await apiSave(planning)) {
            handler.handlerUnset()
            updateVersion()
            return true
        }

        return false
    }

    const deleteProductAll = async () => {
        const planning = produce(getPlanning(), draft => {
            draft.products = []
        })

        if (await apiSave(planning)) {
            handler.handlerUnset()
            updateVersion()
            return true
        }

        return false
    }

    const hasHiddenElements = (itemId) => {
        return getProducts().filter(product => {
            return (product.id === itemId) && product.elements.find(element => element.isHidden)
        }).length > 0
    }

    const unhideElements = async (productId) => {
        const product = getProduct(productId)
        if (!product) return false

        const planning = produce(getPlanning(), draft => {
            draft.products[getProductIndex(productId)].elements.forEach((_, index, elements) => {
                elements[index].isHidden = false
            })
        })

        if (await apiSave(planning)) {
            handler.handlerUnset()
            updateVersion()
            return true
        }

        return false
    }

    const changeProductVariant = async (sku, unitCount, affectSize, applyToAll) => {
        const product = getProduct()
        if (!product) return false

        const quantity = Math.max(1, Math.round(product.quantity / unitCount)) * unitCount

        const planning = produce(getPlanning(), draft => {
            draft.products.forEach((element, index, elements) => {
                if ((applyToAll && element.parentSku === product.parentSku) || element.id === product.id) {
                    elements[index].sku = sku
                    elements[index].quantity = quantity
                    if ((element.quantity != quantity || affectSize)) {
                        elements[index].elements = []
                    }
                }
            })
        })

        if (await apiSave(planning)) {
            handler.handlerUnset()
            updateVersion()
            return true
        }

        return false
    }

    const isWireDuo40MountingSurfaceType = () => {
        const product = getProduct()
        if (!product) return false

        return product.mountingSurfaceType === "wire-duo-40"
    }

    const itemRotateRegular = async () => {
        if (!getProduct()) return false

        const planning = produce(getPlanning(), draft => {
            draft.products = draft.products.map(mapProduct => {

                const itemId = handler.handlerGetItemId()
                const elementIndex = handler.handlerGetElementIndex()

                if (mapProduct.id !== itemId) {
                    return mapProduct
                }

                mapProduct.elements[elementIndex].position = null
                mapProduct.elements[elementIndex].rotation += 90

                const isFullRotation = mapProduct.elements[elementIndex].rotation >= 360
                if (isFullRotation) {
                    mapProduct.elements[elementIndex].rotation = 0
                }

                return mapProduct
            })
        })

        if (await apiSave(planning)) {
            handler.handlerUnset()
            updateVersion()
            return true
        }

        return false
    }

    const itemRotateWireDuo = async () => {
        if (!getProduct()) return false

        const providerId = getProduct().elements[handler.handlerGetElementIndex()].provider

        const planning = produce(getPlanning(), draft => {
            draft.products = draft.products.map(mapProduct => {
                mapProduct.elements = mapProduct.elements.map(mapElement => {
                    if (mapElement.id === providerId) {
                        mapElement.position = null
                        mapElement.rotation = mapElement.rotation + 90
                    }
                    if (mapElement.provider === providerId) {
                        mapElement.position = null
                    }
                    return mapElement
                })
                return mapProduct
            })
        })

        if (await apiSave(planning)) {
            handler.handlerUnset()
            updateVersion()
            return true
        }

        return false
    }

    const getProducts = () => {
        return getPlanning()?.products ?? []
    }

    const getProduct = (productId = null) => {
        productId = productId ? productId : handler.handlerGetItemId()
        return getProducts().find(product => product.id === productId)
    }

    const getProductIndex = (productId = null) => {
        productId = productId ? productId : handler.handlerGetItemId()
        return getProducts().findIndex(product => product.id === productId)
    }

    const addProduct = async (product, mountings) => {
        if (!countPlannings()) {
            await addPlanning([], 'Leer')
        }
        const mounting = product.mountingSurfaces.filter(mounting => mountings.includes(mounting)).shift()
        const productVariant = getVariants(mounting, product).shift()
        const planProduct = {
            parentSku: product.sku,
            sku: productVariant.sku,
            quantity: productVariant.unitCount,
            mountingSurfaceType: mounting,
            elements: []
        }

        const planning = produce(getPlanning(), draft => {
            draft.products.push(planProduct)
        })

        if (await apiSave(planning)) {
            handler.handlerUnset()
            messenger.showSuccess(local.__("Absorber wurden dem Raum hinzugefügt. Ggf. befinden sich die Absorber nicht im Sichtfeld."))
            updateVersion()
            return true
        }

        return false
    }

    const productSetQty = async (quantity, productId = null) => {
        if (quantity < 1) return false

        const product = getProduct(productId)
        if (!product) return false

        const planning = produce(getPlanning(), draft => {
            draft.products[getProductIndex(productId)].quantity = quantity
        })

        if (await apiSave(planning)) {
            handler.handlerUnset()
            messenger.showSuccess(local.__("Absorber wurden dem Raum hinzugefügt. Ggf. befinden sich die Absorber nicht im Sichtfeld."))
            updateVersion()
            return true
        }

        return false
    }

    const swapItem = async (parentSku, sku) => {
        const product = getProduct()
        if (!product) return false

        const planning = produce(getPlanning(), draft => {
            draft.products[getProductIndex()].sku = sku
            draft.products[getProductIndex()].parentSku = parentSku
            draft.products[getProductIndex()].elements = []
        })

        if (await apiSave(planning)) {
            handler.handlerUnset()
            updateVersion()
            return true
        }

        return false
    }

    const updatePosition = (updatedProducts, updateVersionFlag = true) => {
        if (isWallToWallMove(getProducts(), updatedProducts)) {
            const yes = async () => {
                const planning = produce(getPlanning(), draft => {
                    draft.products.forEach((product) => {
                        product.elements.forEach((element) => {
                            const newMountingSurface = getProductElementProp(updatedProducts, product.id, element.id, 'mountingSurface')
                            const currentMountingSurface = element.mountingSurface
                            if (newMountingSurface.index !== currentMountingSurface.index) {
                                currentMountingSurface.index = newMountingSurface.index
                                element.position = null
                            }
                        })
                    })
                })

                await apiSave(planning)
                updateVersion()
            }
            const no = () => {
                state.setState(produce(state.getCurrent(), draft => {
                    draft.version = draft.version + 1
                }))
            }
            messenger.showPrompt(local.__("Möchten Sie den Absorber auf die benachbarte Wand verschieben?"), yes, no)
        } else {
            state.setState(produce(state.getCurrent(), draft => {
                draft.plannings[getPlanningIndex()].products.forEach((product, productIndex) => {
                    product.elements.forEach((element, elementIndex) => {
                        draft.plannings[getPlanningIndex()].products[productIndex].elements[elementIndex].position =
                            getProductElementProp(updatedProducts, product.id, element.id, 'position')
                    })
                })
                if (updateVersionFlag) {
                    updateVersion()
                }
            }))
        }
    }

    const addPlanning = async (productsRaw, name) => {
        const products = productsRaw.map(element => ({
            sku: element.sku,
            parentSku: element.parentSku,
            quantity: element.quantity,
            mountingSurfaceType: element.mountingSurfaceType,
            elements: []
        }))

        if (await apiSave({id: null, name: name, products: products})) {
            if (countPlannings() === 1) {
                handler.handlerUnset()
                updateVersion()
            }
            return true
        }

        return false
    }

    const getPlannings = () => {
        return state.getCurrent().plannings.map((solution, index) => {
            return {
                index: index,
                name: solution.name,
                updatedAt: solution.updatedAt,
                isPremium: solution.isPremium,
                isActive: index === getPlanningIndex(),
            }
        })
    }

    const getPlanning = () => {
        return state.getCurrent().plannings[getPlanningIndex()]
            ? state.getCurrent().plannings[getPlanningIndex()]
            : null
    }

    const countPlannings = () => {
        return state.getCurrent().plannings.length
    }

    const hasPlannings = () => {
        return countPlannings() > 0
    }

    const setPlanningIndex = (index) => {
        if (index < 0 || index > (countPlannings() - 1)) return false

        state.setState(produce(state.getCurrent(), draft => {
            draft.solutionIndex = index
        }))

        updateVersion()
        return true
    }

    const getPlanningIndex = () => {
        return state.getCurrent().solutionIndex
    }

    const canReset = () => {
        return getPlanning().initialRevision && getPlanning().initialRevision !== getId()
    }

    const resetPlanning = async () => {
        if (!canReset()) {
            return false
        }

        const planing = await apiSolutionLoad(getPlanning().initialRevision, local.getLang(true))

        if (planing) {
            state.setState(produce(state.getCurrent(), draft => {
                draft.plannings[getPlanningIndex()] = planing
            }))

            handler.handlerUnset()
            updateVersion()
            return true
        }

        return false
    }

    const deletePlanning = async () => {
        const plannings = produce(state.getCurrent(), draft => {
            draft.plannings = draft.plannings.filter((_, index) => index !== getPlanningIndex())
        })
        state.setState(plannings)
        handler.handlerUnset()
        setPlanningIndex(0)
        updateVersion()
    }

    const setPlanningName = (name) => {
        const plannings = produce(state.getCurrent(), draft => {
            draft.plannings[getPlanningIndex()].name = name
        })

        state.setState(plannings)
    }

    const focusSet = (itemId) => {
        document.dispatchEvent(new CustomEvent('focusItem', {detail: itemId}))
    }

    return {
        save,
        reset,
        setPlannings,
        getId,
        getVersion,
        getRoomId,
        setRoomId,
        getItem: getProduct,
        addItem: addProduct,
        getItems: getProducts,
        setItemQty: productSetQty,
        swapItem,
        focusSet,
        increaseProductQty: productIncreaseQty,
        itemHideElement: hideElement,
        itemRemove: deleteProduct,
        itemRemoveType: deleteProductByType,
        itemRemoveAll: deleteProductAll,
        itemHasHiddenElements: hasHiddenElements,
        itemUnhiddenElements: unhideElements,
        itemChangeVariant: changeProductVariant,
        isWireDuo40MountingSurfaceType,
        itemRotateRegular,
        itemRotateWireDuo,
        updatePosition,
        addPlanning,
        getSolutionList: getPlannings,
        countSolution: countPlannings,
        hasPlannings,
        getCurrentSolution: getPlanning,
        setCurrentSolutionIndex: setPlanningIndex,
        resetCurrentSolution: resetPlanning,
        removeCurrentSolution: deletePlanning,
        saveCurrentSolution: save,
        getPlanningIds,
        ...handler,
        setPlanningIndex,
        countPlannings,
        setPlanningName,
        updateVersion,
        getSavedRoom, setCode, getCode
    }
}
